/**
 * Fattibilità: normativa vs copertura ideale vs organico attuale.
 */
import React, { useState } from "react";
import { useApi } from "../hooks/useApi";
import RsaSelect from "../components/RsaSelect";
import {
  Card,
  Badge,
  Spinner,
  ErrorBox,
  StatCard,
  SimpleTable,
  WarningList,
} from "../components/ui/basics";

function codiciDiRiga(riga) {
  const set = new Set();
  for (const d of riga.dettaglioNormativa || []) set.add(d.codice);
  for (const d of riga.dettaglioCopertura || []) set.add(d.codice);
  for (const d of riga.dettaglioOrganico || []) set.add(d.codice);
  return [...set];
}

/**
 * Punto 4 dello scoping "Motore di generazione automatica copertura":
 * confronto composizioni organico (teste in ordinario vs meno teste con
 * straordinario) per la stessa mansione, a parità di ore richieste. Sola
 * lettura/analisi — non salva né suggerisce automaticamente nulla, è uno
 * strumento di decisione per chi configura l'organico.
 */
// Etichetta/stato della fonte di costo usata per QUESTA riga, vedi
// fabbisognoService.js::fattibilitaForRsa (campo riga.fonteCosto). Con
// fonteCosto=reale il costo medio orario reale mescola già ordinario e
// straordinario (nessuna scomposizione per fascia possibile dal CSV
// gestionale paghe) — il confronto "evita straordinario" resta calcolabile
// ma diventa cost-neutral per quelle mansioni, non un bug.
const FONTE_COSTO_BADGE = {
  ccnl: { status: "default", label: "CCNL teorico" },
  reale: { status: "ok", label: "Costo reale" },
  ccnl_fallback: {
    status: "attenzione",
    label: "CCNL (dato reale insufficiente)",
  },
  fallback_generico: { status: "attenzione", label: "Fallback generico" },
};

function DettaglioCosto({ riga }) {
  const { composizioni, consigliata } = riga.composizioniCosto || {};
  if (!composizioni?.length) return null;
  const fonte = FONTE_COSTO_BADGE[riga.fonteCosto] || FONTE_COSTO_BADGE.ccnl;
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Composizioni organico — {riga.mansione}
          <Badge status={fonte.status}>{fonte.label}</Badge>
        </span>
      }
      subtitle="A parità di ore richieste, più teste entro l'orario ordinario vs meno teste con una quota di straordinario (costo/ora da Impostazioni → Costi personale, o dal costo reale rilevato se selezionato sopra)."
    >
      <SimpleTable
        rowKey={(c) => c.teste}
        columns={[
          { key: "teste", label: "Teste", align: "right" },
          {
            key: "orePersonaSett",
            label: "Ore/persona",
            align: "right",
          },
          {
            key: "oreStraordinarioTotali",
            label: "Straordinario tot.",
            align: "right",
            render: (c) => c.oreStraordinarioTotali.toLocaleString("it-IT"),
          },
          {
            key: "costoTotaleSett",
            label: "Costo/sett",
            align: "right",
            render: (c) =>
              `€ ${c.costoTotaleSett.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`,
          },
          {
            key: "stato",
            label: "",
            render: (c) => (
              <div className="flex gap-1 justify-end">
                {!c.realizzabile && (
                  <Badge status="critico">Irrealizzabile</Badge>
                )}
                {consigliata && c.teste === consigliata.teste && (
                  <Badge status="ok">Consigliata</Badge>
                )}
              </div>
            ),
          },
        ]}
        rows={composizioni}
      />
    </Card>
  );
}

function DettaglioGruppo({ riga }) {
  const codici = codiciDiRiga(riga);
  return (
    <Card
      title={`Dettaglio per codice — ${riga.mansione}`}
      subtitle="Sovrapponibilità mansioni: contributo di ciascun codice sorgente al gruppo"
    >
      <SimpleTable
        rowKey={(r) => r.codice}
        columns={[
          { key: "codice", label: "Codice" },
          {
            key: "normativa",
            label: "Normativa",
            align: "right",
            render: (r) =>
              (
                riga.dettaglioNormativa?.find((d) => d.codice === r.codice)
                  ?.valore || 0
              ).toLocaleString("it-IT"),
          },
          {
            key: "copertura",
            label: "Copertura",
            align: "right",
            render: (r) => {
              const d = riga.dettaglioCopertura?.find(
                (x) => x.codice === r.codice,
              );
              if (!d) return "0";
              return `${d.valore.toLocaleString("it-IT")}${d.tipo === "parziale" ? ` (max ${d.pesoPct}%)` : ""}`;
            },
          },
          {
            key: "organico",
            label: "Organico",
            align: "right",
            render: (r) => {
              const d = riga.dettaglioOrganico?.find(
                (x) => x.codice === r.codice,
              );
              if (!d) return "0";
              return `${d.valore.toLocaleString("it-IT")}${d.tipo === "parziale" ? ` (max ${d.pesoPct}%)` : ""}`;
            },
          },
        ]}
        rows={codici.map((codice) => ({ codice }))}
      />
    </Card>
  );
}

export default function FattibilitaPage() {
  const [rsa, setRsa] = useState(null);
  const [pattern, setPattern] = useState("");
  const [gruppoEspanso, setGruppoEspanso] = useState(null);
  const [costoEspanso, setCostoEspanso] = useState(null);
  const [confrontaCosto, setConfrontaCosto] = useState(false);
  // "ccnl" (default, invariato) | "reale" — vedi services/costoRealeMap.js.
  // Query param solo quando confrontaCosto è attivo: nessun impatto quando
  // il confronto costi non è richiesto.
  const [fonteCosto, setFonteCosto] = useState("ccnl");

  const { data: patterns } = useApi("/planning/patterns");
  const endpoint = rsa
    ? `/fattibilita/${encodeURIComponent(rsa)}?${new URLSearchParams({
        ...(pattern ? { pattern } : {}),
        ...(confrontaCosto ? { confrontaCosto: "1" } : {}),
        ...(confrontaCosto && fonteCosto === "reale"
          ? { fonteCosto: "reale" }
          : {}),
      })}`
    : null;
  const { data, loading, error, reload } = useApi(endpoint, [
    pattern,
    confrontaCosto,
    fonteCosto,
  ]);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Fattibilità</h2>
      <p className="text-sm text-gray-500">
        Confronto tra obbligo normativo, copertura ideale e organico attuale —
        con il dimensionamento (quante persone servono) per il pattern scelto.
      </p>

      <div className="flex gap-3 items-center flex-wrap">
        <RsaSelect value={rsa} onChange={setRsa} />
        <select
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
        >
          <option value="">Pattern: solo contratto</option>
          {(patterns?.items || []).map((p) => (
            <option key={p.codice} value={p.codice}>
              {p.nome}
            </option>
          ))}
        </select>
        <label className="text-sm flex items-center gap-1.5 text-gray-600">
          <input
            type="checkbox"
            checked={confrontaCosto}
            onChange={(e) => setConfrontaCosto(e.target.checked)}
          />
          Confronta composizioni organico (evita straordinario)
        </label>
        {confrontaCosto && (
          <select
            value={fonteCosto}
            onChange={(e) => setFonteCosto(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
            title="Fonte del costo/ora per mansione usata nel confronto composizioni"
          >
            <option value="ccnl">Costo: CCNL teorico</option>
            <option value="reale">Costo: reale rilevato (CSV)</option>
          </select>
        )}
      </div>

      {!rsa && (
        <p className="text-sm text-gray-400 py-8 text-center">
          Seleziona una RSA per il report.
        </p>
      )}
      {loading && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      {data && data.status === "ok" && (
        <>
          <WarningList warnings={data.warnings} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Ore richieste (max normativa/copertura)"
              value={data.fattibilita.totale.oreRichieste.toLocaleString(
                "it-IT",
              )}
              unit="h/sett"
            />
            <StatCard
              label="Ore disponibili (organico)"
              value={data.fattibilita.totale.oreDisponibili.toLocaleString(
                "it-IT",
              )}
              unit="h/sett"
            />
            <StatCard
              label="Gap"
              value={data.fattibilita.totale.gapOreSett.toLocaleString("it-IT")}
              unit="h/sett"
              tone={data.fattibilita.totale.gapOreSett >= 0 ? "good" : "bad"}
            />
            <StatCard
              label="Persone mancanti"
              value={data.fattibilita.totale.personeMancanti}
              tone={
                data.fattibilita.totale.personeMancanti > 0 ? "bad" : "good"
              }
            />
          </div>

          <Card
            title="Dettaglio per mansione"
            subtitle={`${data.regione} · ${data.occupazione} ospiti · contratto ${data.contratto?.nome}${data.pattern ? ` · pattern ${data.pattern.nome}` : ""} · organico: ${data.organico.dipendenti} dipendenti (${data.organico.liberiProfessionisti} LP esclusi)`}
          >
            <SimpleTable
              rowKey={(r) => r.mansione}
              columns={[
                { key: "mansione", label: "Mansione" },
                {
                  key: "oreNormativa",
                  label: "Normativa",
                  align: "right",
                  render: (r) => r.oreNormativa.toLocaleString("it-IT"),
                },
                {
                  key: "oreCopertura",
                  label: "Copertura",
                  align: "right",
                  render: (r) => r.oreCopertura.toLocaleString("it-IT"),
                },
                {
                  key: "oreDisponibili",
                  label: "Organico",
                  align: "right",
                  render: (r) => r.oreDisponibili.toLocaleString("it-IT"),
                },
                {
                  key: "gapOreSett",
                  label: "Gap h/sett",
                  align: "right",
                  render: (r) => (
                    <span
                      className={
                        r.gapOreSett < 0 ? "text-red-600" : "text-green-700"
                      }
                    >
                      {r.gapOreSett.toLocaleString("it-IT")}
                    </span>
                  ),
                },
                {
                  key: "personeMancanti",
                  label: "Persone mancanti",
                  align: "right",
                },
                {
                  key: "status",
                  label: "Stato",
                  render: (r) => <Badge status={r.status} />,
                },
                {
                  key: "dettaglio",
                  label: "",
                  render: (r) => {
                    const n = codiciDiRiga(r).length;
                    if (n <= 1) return null;
                    return (
                      <button
                        onClick={() =>
                          setGruppoEspanso(
                            gruppoEspanso === r.mansione ? null : r.mansione,
                          )
                        }
                        className="text-xs text-primary-600 hover:underline"
                      >
                        {gruppoEspanso === r.mansione ? "▾" : "▸"} {n} codici
                      </button>
                    );
                  },
                },
                ...(confrontaCosto
                  ? [
                      {
                        key: "costo",
                        label: "",
                        render: (r) => {
                          if (!r.composizioniCosto?.composizioni?.length)
                            return null;
                          const attiva = costoEspanso === r.mansione;
                          return (
                            <button
                              onClick={() =>
                                setCostoEspanso(attiva ? null : r.mansione)
                              }
                              className="text-xs text-primary-600 hover:underline"
                            >
                              {attiva ? "▾" : "▸"} composizioni
                            </button>
                          );
                        },
                      },
                    ]
                  : []),
              ]}
              rows={data.fattibilita.righe}
            />
          </Card>

          {gruppoEspanso &&
            (() => {
              const riga = data.fattibilita.righe.find(
                (r) => r.mansione === gruppoEspanso,
              );
              return riga ? <DettaglioGruppo riga={riga} /> : null;
            })()}

          {costoEspanso &&
            (() => {
              const riga = data.fattibilita.righe.find(
                (r) => r.mansione === costoEspanso,
              );
              return riga ? <DettaglioCosto riga={riga} /> : null;
            })()}

          {data.copertura.source === "default" && (
            <p className="text-xs text-gray-400">
              Nota: nessun template di copertura configurato (entità Dataverse
              da creare) — la colonna Copertura è vuota.
            </p>
          )}
        </>
      )}
      {data && data.status !== "ok" && (
        <ErrorBox message={`RSA non calcolabile: ${data.status}`} />
      )}
    </div>
  );
}
